/**
 * Shared reader for a product's token stylesheet (KORA-112, ATLAS-27). The CSS file stays the
 * single source; TypeScript reads its first :root block at build time (`?raw` import in the
 * product's own module), so Naive UI overrides can never drift from the CSS.
 */

export type Tokens = Readonly<Record<string, string>>

/**
 * Reads `--name: value;` pairs from the first block whose selector is exactly `selector`
 * (e.g. `:root[data-theme='light']`). Comments are ignored.
 */
export function parseBlockTokens(css: string, selector: string): Tokens {
  const source = css.replace(/\/\*[\s\S]*?\*\//g, '')
  const escaped = selector.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
  const found = new RegExp(`${escaped}\\s*\\{`).exec(source)
  if (!found) throw new Error(`Token stylesheet has no ${selector} block`)
  const open = found.index + found[0].length - 1
  const close = source.indexOf('}', open)
  if (open < 0 || close < 0) throw new Error(`Token stylesheet ${selector} block is not closed`)
  const tokens: Record<string, string> = {}
  for (const match of source
    .slice(open + 1, close)
    .matchAll(/(--[a-z0-9-]+)\s*:\s*([^;]+)(?:;|$)/g)) {
    const name = match[1]
    const value = match[2]
    if (name !== undefined && value !== undefined) tokens[name] = value.trim()
  }
  return tokens
}

/** Reads `--name: value;` pairs from the first :root block. Comments are ignored. */
export function parseRootTokens(css: string): Tokens {
  return parseBlockTokens(css, ':root')
}

/** A reader for concrete values: throws when a token is missing or is a var() reference. */
export function tokenReader(product: string, tokens: Tokens): (name: string) => string {
  return (name: string): string => {
    const value = tokens[name]
    if (value === undefined) throw new Error(`${product} token ${name} is not defined`)
    if (value.startsWith('var('))
      throw new Error(`${product} token ${name} is an alias, not a value`)
    return value
  }
}
